import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import { Box, Button, Typography } from '@mui/material'

import { useAppContext } from '@/hooks'

import { PackageIcon } from './PackageIcon'
import { StatusPill } from './StatusPill'
import { DeploymentPackageRecord } from './types'

type PackageListProps = {
	onCreate: () => void
	onOpen: (id: string) => void
	packages: DeploymentPackageRecord[]
}

export const PackageList = ({ onCreate, onOpen, packages }: PackageListProps) => {
	const { t } = useAppContext()
	const columns = [
		t('DEPLOYMENT_PACKAGES_COL_PACKAGE'),
		t('DEPLOYMENT_PACKAGES_COL_STATUS'),
		t('DEPLOYMENT_PACKAGES_COL_BRANCH'),
		t('DEPLOYMENT_PACKAGES_COL_OWNER'),
	]
	const rowColumns = '2fr 140px 1.2fr 1.4fr'

	return (
		<Box
			sx={{
				bgcolor: '#F7F7F7',
				display: 'flex',
				flex: 1,
				flexDirection: 'column',
				height: '100%',
				minHeight: 0,
				minWidth: 0,
			}}
		>
			<Box
				sx={{
					alignItems: 'center',
					bgcolor: '#fff',
					borderBottom: '1px solid #E4E4E4',
					display: 'flex',
					gap: '12px',
					pb: '16px',
					pt: '20px',
					px: '26px',
				}}
			>
				<PackageIcon sx={{ color: '#1F4954', fontSize: 20 }} />
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ color: '#1F4954', fontSize: '18px', fontWeight: 800 }}>
						{t('ACTIVITY_BAR_DEPLOYMENT_PACKAGES')}
					</Typography>
					<Typography sx={{ color: '#9A9A9A', fontSize: '12px' }}>
						{t('DEPLOYMENT_PACKAGES_SUBTITLE')}
					</Typography>
				</Box>
				<Button
					variant="contained"
					disableElevation
					startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
					onClick={onCreate}
					data-testid="deployment-packages-create"
					sx={{ borderRadius: '9px', height: 38, px: '16px' }}
				>
					{t('DEPLOYMENT_PACKAGES_CREATE')}
				</Button>
			</Box>

			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '18px 26px' }}>
				<Box
					sx={{
						color: '#9A9A9A',
						display: 'grid',
						fontSize: '10px',
						fontWeight: 700,
						gap: '12px',
						gridTemplateColumns: rowColumns,
						letterSpacing: '.05em',
						pb: '8px',
						px: '16px',
						textTransform: 'uppercase',
					}}
				>
					{columns.map((header) => (
						<Box key={header}>{header}</Box>
					))}
				</Box>
				{packages.length === 0 ? (
					<Box
						data-testid="deployment-packages-empty"
						sx={{
							bgcolor: '#fff',
							border: '1px solid #E7E7E7',
							borderRadius: '10px',
							color: '#B0B0B0',
							fontSize: '13px',
							p: '40px',
							textAlign: 'center',
						}}
					>
						{t('DEPLOYMENT_PACKAGES_EMPTY')}
					</Box>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						{packages.map((item) => (
							<Box
								key={item.id}
								onClick={() => onOpen(item.id)}
								data-testid={`deployment-package-row-${item.id}`}
								sx={{
									alignItems: 'center',
									bgcolor: '#fff',
									border: '1px solid #E7E7E7',
									borderRadius: '10px',
									cursor: 'pointer',
									display: 'grid',
									gap: '12px',
									gridTemplateColumns: rowColumns,
									px: '16px',
									py: '12px',
									'&:hover': {
										borderColor: '#1F4954',
										boxShadow: '0 2px 10px rgba(31,73,84,0.08)',
									},
								}}
							>
								<Box sx={{ alignItems: 'center', display: 'flex', gap: '10px', minWidth: 0 }}>
									<PackageIcon sx={{ color: '#5B43C4', fontSize: 17 }} />
									<Box sx={{ minWidth: 0 }}>
										<Box
											sx={{
												color: '#1F4954',
												fontFamily: 'ui-monospace, monospace',
												fontSize: '13.5px',
												fontWeight: 700,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
											}}
										>
											{item.name}
										</Box>
										{item.description ? (
											<Box
												sx={{
													color: '#9A9A9A',
													fontSize: '10.5px',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												{item.description}
											</Box>
										) : null}
									</Box>
								</Box>
								<StatusPill status={item.status} />
								<Box
									sx={{
										color: '#6A6A6A',
										fontSize: '12.5px',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{item.branchName}
								</Box>
								<Box
									sx={{
										color: '#6A6A6A',
										fontSize: '12.5px',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{item.owner}
								</Box>
							</Box>
						))}
					</Box>
				)}
			</Box>
		</Box>
	)
}
